import { formatCurrency, formatInteger } from '@/utils/numberFormat';

const DEFAULT_CURRENCY = 'MXN';

const toNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toSafeCount = (value) => {
  return Math.max(0, Math.trunc(toNumber(value)));
};

const normalizeCurrencyCode = (value) => {
  const code = String(value || '').trim().toUpperCase();
  return code === '' ? DEFAULT_CURRENCY : code;
};

const orderCurrencyTotals = (totals = []) => {
  const safeTotals = Array.isArray(totals) ? totals : [];

  return [...safeTotals].sort((left, right) => {
    const leftCode = normalizeCurrencyCode(left?.currency);
    const rightCode = normalizeCurrencyCode(right?.currency);

    if (leftCode === DEFAULT_CURRENCY && rightCode !== DEFAULT_CURRENCY) {
      return -1;
    }

    if (rightCode === DEFAULT_CURRENCY && leftCode !== DEFAULT_CURRENCY) {
      return 1;
    }

    return leftCode.localeCompare(rightCode);
  });
};

const normalizeCurrencyTotals = (rawTotals = [], fallbackTotal = 0) => {
  const grouped = {};

  if (Array.isArray(rawTotals)) {
    rawTotals.forEach((item) => {
      const currency = normalizeCurrencyCode(item?.currency);
      const total = toNumber(item?.total ?? item?.amount);
      grouped[currency] = (grouped[currency] || 0) + total;
    });
  } else if (rawTotals && typeof rawTotals === 'object') {
    Object.entries(rawTotals).forEach(([currencyKey, total]) => {
      const currency = normalizeCurrencyCode(currencyKey);
      grouped[currency] = (grouped[currency] || 0) + toNumber(total);
    });
  }

  if (Object.keys(grouped).length === 0 && toNumber(fallbackTotal) !== 0) {
    grouped[DEFAULT_CURRENCY] = toNumber(fallbackTotal);
  }

  return orderCurrencyTotals(
    Object.entries(grouped)
      .map(([currency, total]) => ({
        currency,
        total: toNumber(total),
      }))
      .filter((item) => Number.isFinite(item.total))
  );
};

const normalizeCurrencyCounts = (rawCounts = [], fallbackCount = 0, fallbackCurrency = DEFAULT_CURRENCY) => {
  const grouped = {};

  if (Array.isArray(rawCounts)) {
    rawCounts.forEach((item) => {
      const currency = normalizeCurrencyCode(item?.currency);
      const count = toSafeCount(item?.count ?? item?.total ?? item?.amount);
      grouped[currency] = (grouped[currency] || 0) + count;
    });
  } else if (rawCounts && typeof rawCounts === 'object') {
    Object.entries(rawCounts).forEach(([currencyKey, count]) => {
      const currency = normalizeCurrencyCode(currencyKey);
      grouped[currency] = (grouped[currency] || 0) + toSafeCount(count);
    });
  }

  const safeFallbackCount = toSafeCount(fallbackCount);
  if (Object.keys(grouped).length === 0 && safeFallbackCount > 0) {
    grouped[normalizeCurrencyCode(fallbackCurrency)] = safeFallbackCount;
  }

  return grouped;
};

const buildAverageByCurrencyFromTotals = (totalsByCurrency = [], countsByCurrency = {}) => {
  const normalizedTotals = normalizeCurrencyTotals(totalsByCurrency);
  const countMap = normalizeCurrencyCounts(countsByCurrency);

  return orderCurrencyTotals(
    normalizedTotals.map((item) => {
      const currency = normalizeCurrencyCode(item?.currency);
      const count = toSafeCount(countMap[currency]);
      const average = count > 0 ? toNumber(item?.total) / count : 0;

      return {
        currency,
        total: toNumber(average),
      };
    })
  );
};

const normalizeSummaryByCurrency = (rawSummaryByCurrency = [], fallbackTotalsByCurrency = []) => {
  const grouped = {};

  if (Array.isArray(rawSummaryByCurrency)) {
    rawSummaryByCurrency.forEach((item) => {
      const currency = normalizeCurrencyCode(item?.currency);
      const sourceSummary = item?.summary && typeof item.summary === 'object'
        ? item.summary
        : item;
      const normalizedSummary = normalizeSummarySection(sourceSummary, item?.total);

      if (!grouped[currency]) {
        grouped[currency] = createSummarySection();
      }

      grouped[currency].subtotal += normalizedSummary.subtotal;
      grouped[currency].transferred_taxes += normalizedSummary.transferred_taxes;
      grouped[currency].transfered_taxes += normalizedSummary.transfered_taxes;
      grouped[currency].withheld_taxes += normalizedSummary.withheld_taxes;
      grouped[currency].total += normalizedSummary.total;
    });
  } else if (rawSummaryByCurrency && typeof rawSummaryByCurrency === 'object') {
    Object.entries(rawSummaryByCurrency).forEach(([currencyCode, summary]) => {
      const currency = normalizeCurrencyCode(currencyCode);
      const normalizedSummary = normalizeSummarySection(summary, summary?.total);

      grouped[currency] = {
        subtotal: normalizedSummary.subtotal,
        transferred_taxes: normalizedSummary.transferred_taxes,
        transfered_taxes: normalizedSummary.transfered_taxes,
        withheld_taxes: normalizedSummary.withheld_taxes,
        total: normalizedSummary.total,
      };
    });
  }

  const normalizedFallbackTotals = normalizeCurrencyTotals(fallbackTotalsByCurrency);
  normalizedFallbackTotals.forEach((item) => {
    const currency = normalizeCurrencyCode(item?.currency);
    if (!grouped[currency]) {
      grouped[currency] = normalizeSummarySection({}, item?.total);
    }
  });

  return orderCurrencyTotals(
    Object.entries(grouped).map(([currency, summary]) => ({
      currency,
      summary: normalizeSummarySection(summary, summary?.total),
      total: toNumber(summary?.total),
    }))
  );
};

const formatCurrencyByCode = (value, currency) => {
  return formatCurrency(value, {
    currency: normalizeCurrencyCode(currency),
  });
};

const buildInvoicedBreakdownItems = (summary, labels, currency) => {
  const subtotalLabel = String(labels.subtotal || 'Subtotal').trim();
  const transferredTaxesLabel = String(labels.transferredTaxes || 'Impuestos').trim();
  const withheldTaxesLabel = String(labels.withheldTaxes || 'Retenciones').trim();

  return [
    {
      label: `${subtotalLabel}:`,
      value: formatCurrencyByCode(summary.subtotal, currency),
      tone: 'base',
    },
    {
      label: `+ ${transferredTaxesLabel}:`,
      value: formatCurrencyByCode(summary.transferred_taxes, currency),
      tone: 'plus',
    },
    {
      label: `- ${withheldTaxesLabel}:`,
      value: formatCurrencyByCode(summary.withheld_taxes, currency),
      tone: 'minus',
    },
  ];
};

const buildTotalInvoicedKpiCards = (metricsValues, labels, totalInvoicedByCurrency, totalInvoicedSummaryByCurrency) => {
  const normalizedTotals = totalInvoicedByCurrency.length > 0
    ? totalInvoicedByCurrency
    : [{ currency: DEFAULT_CURRENCY, total: toNumber(metricsValues?.totalInvoiced) }];

  const summaryByCurrencyMap = new Map(
    (Array.isArray(totalInvoicedSummaryByCurrency) ? totalInvoicedSummaryByCurrency : [])
      .map((item) => {
        const currency = normalizeCurrencyCode(item?.currency);
        return [currency, normalizeSummarySection(item?.summary, item?.total)];
      })
  );

  return normalizedTotals.map((item) => {
    const currency = normalizeCurrencyCode(item?.currency);
    const total = toNumber(item?.total);
    const summary = summaryByCurrencyMap.get(currency)
      || normalizeSummarySection(metricsValues?.totalInvoicedSummary, total);

    return {
      title: `${labels.totalInvoiced || 'Total facturado'} ${currency}`,
      value: formatCurrencyByCode(total, currency),
      valueMetaItems: buildInvoicedBreakdownItems(summary, labels, currency),
      valueScale: 0.88,
      icon: 'fa-file-text-o',
      iconBg: '#ecf3ff',
      iconColor: '#175baf',
    };
  });
};

const buildCurrencySegments = (totalsByCurrency = [], fallbackTotal = 0) => {
  const normalizedTotals = normalizeCurrencyTotals(totalsByCurrency, fallbackTotal);

  if (normalizedTotals.length === 0) {
    return [formatCurrency(fallbackTotal || 0)];
  }

  const shouldShowCode = normalizedTotals.length > 1;

  return normalizedTotals.map((item) => {
    const currencyCode = normalizeCurrencyCode(item?.currency);
    const amount = formatCurrency(item?.total || 0);

    if (!shouldShowCode && currencyCode === DEFAULT_CURRENCY) {
      return amount;
    }

    return `${currencyCode} ${amount}`;
  });
};

const isCanceledInvoice = (row) => {
  const status = String(row?.status_key || row?.status || '').trim().toLowerCase();

  if (['canceled', 'cancelada', 'cancelado'].includes(status)) {
    return true;
  }

  return Number(row?.canceled || 0) === 1;
};

const createSummarySection = () => ({
  subtotal: 0,
  transferred_taxes: 0,
  transfered_taxes: 0,
  withheld_taxes: 0,
  total: 0,
});

const normalizeSummarySection = (section, fallbackTotal = 0) => {
  const source = section && typeof section === 'object' ? section : {};
  const transferredTaxes = toNumber(source.transferred_taxes ?? source.transfered_taxes);
  const total = toNumber(source.total ?? fallbackTotal);
  const subtotalCandidate = source.subtotal;
  const subtotal = subtotalCandidate == null
    ? toNumber(total - transferredTaxes + toNumber(source.withheld_taxes))
    : toNumber(subtotalCandidate);

  return {
    subtotal,
    transferred_taxes: transferredTaxes,
    transfered_taxes: transferredTaxes,
    withheld_taxes: toNumber(source.withheld_taxes),
    total,
  };
};

export const createInitialInvoicesMetricsValues = () => ({
  totalInvoiced: 0,
  invoiceCount: 0,
  invoiceAverage: 0,
  totalCollected: 0,
  pendingBalance: 0,
  overdueInvoices: 0,
  totalInvoicedSummary: createSummarySection(),
  totalInvoicedSummaryByCurrency: [],
  totalInvoicedByCurrency: [],
  invoiceAverageByCurrency: [],
  invoiceCountByCurrency: {},
  totalCollectedByCurrency: [],
  pendingBalanceByCurrency: [],
});

export const buildInvoicesMetricsFromRows = (rows) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const today = new Date();

  const totals = safeRows.reduce((acc, row) => {
    if (isCanceledInvoice(row)) {
      return acc;
    }

    const total = toNumber(row?.total);
    const transferredTaxes = toNumber(row?.transferred_taxes ?? row?.transfered_taxes ?? row?.taxes);
    const withheldTaxes = toNumber(row?.withheld_taxes);
    const hasSubtotal = row && Object.prototype.hasOwnProperty.call(row, 'subtotal');
    const subtotal = hasSubtotal
      ? toNumber(row?.subtotal) - toNumber(row?.discounts)
      : toNumber(total - transferredTaxes + withheldTaxes);
    const balance = toNumber(row?.balance);
    const currency = normalizeCurrencyCode(row?.currency);
    const dueDate = new Date(String(row?.due_date || row?.date || ''));

    acc.totalInvoiced += total;
    acc.invoiceCount += 1;
    acc.totalInvoicedSummary.subtotal += subtotal;
    acc.totalInvoicedSummary.transferred_taxes += transferredTaxes;
    acc.totalInvoicedSummary.transfered_taxes += transferredTaxes;
    acc.totalInvoicedSummary.withheld_taxes += withheldTaxes;
    acc.totalInvoicedSummary.total += total;
    acc.totalInvoicedByCurrencyMap[currency] = (acc.totalInvoicedByCurrencyMap[currency] || 0) + total;
    acc.invoiceCountByCurrencyMap[currency] = (acc.invoiceCountByCurrencyMap[currency] || 0) + 1;

    if (!acc.totalInvoicedSummaryByCurrencyMap[currency]) {
      acc.totalInvoicedSummaryByCurrencyMap[currency] = createSummarySection();
    }

    acc.totalInvoicedSummaryByCurrencyMap[currency].subtotal += subtotal;
    acc.totalInvoicedSummaryByCurrencyMap[currency].transferred_taxes += transferredTaxes;
    acc.totalInvoicedSummaryByCurrencyMap[currency].transfered_taxes += transferredTaxes;
    acc.totalInvoicedSummaryByCurrencyMap[currency].withheld_taxes += withheldTaxes;
    acc.totalInvoicedSummaryByCurrencyMap[currency].total += total;

    const paidAmount = Math.max(total - balance, 0);
    acc.totalCollected += paidAmount;
    acc.totalCollectedByCurrencyMap[currency] = (acc.totalCollectedByCurrencyMap[currency] || 0) + paidAmount;

    acc.pendingBalance += Math.max(balance, 0);
    acc.pendingBalanceByCurrencyMap[currency] = (acc.pendingBalanceByCurrencyMap[currency] || 0) + Math.max(balance, 0);

    if (!Number.isNaN(dueDate.getTime()) && dueDate < today && balance > 0) {
      acc.overdueInvoices += 1;
    }

    return acc;
  }, {
    ...createInitialInvoicesMetricsValues(),
    totalInvoicedByCurrencyMap: {},
    invoiceCountByCurrencyMap: {},
    totalCollectedByCurrencyMap: {},
    pendingBalanceByCurrencyMap: {},
    totalInvoicedSummaryByCurrencyMap: {},
  });

  Object.entries(totals.totalInvoicedSummaryByCurrencyMap).forEach(([currency, summary]) => {
    const currencyCode = normalizeCurrencyCode(currency);
    totals.totalInvoicedSummaryByCurrencyMap[currencyCode] = normalizeSummarySection(summary, summary?.total);
  });

  const totalInvoicedByCurrency = normalizeCurrencyTotals(totals.totalInvoicedByCurrencyMap, totals.totalInvoiced);
  const invoiceCount = toSafeCount(totals.invoiceCount);
  const invoiceAverage = invoiceCount > 0 ? toNumber(totals.totalInvoiced / invoiceCount) : 0;
  const invoiceCountByCurrency = normalizeCurrencyCounts(
    totals.invoiceCountByCurrencyMap,
    invoiceCount,
    totalInvoicedByCurrency[0]?.currency || DEFAULT_CURRENCY,
  );
  const invoiceAverageByCurrency = buildAverageByCurrencyFromTotals(totalInvoicedByCurrency, invoiceCountByCurrency);

  return {
    totalInvoiced: totals.totalInvoiced,
    invoiceCount,
    invoiceAverage,
    totalCollected: totals.totalCollected,
    pendingBalance: totals.pendingBalance,
    overdueInvoices: totals.overdueInvoices,
    totalInvoicedSummary: totals.totalInvoicedSummary,
    totalInvoicedSummaryByCurrency: normalizeSummaryByCurrency(
      Object.entries(totals.totalInvoicedSummaryByCurrencyMap).map(([currency, summary]) => ({
        currency,
        summary,
      })),
      Object.entries(totals.totalInvoicedByCurrencyMap).map(([currency, total]) => ({
        currency,
        total,
      }))
    ),
    totalInvoicedByCurrency,
    invoiceAverageByCurrency,
    invoiceCountByCurrency,
    totalCollectedByCurrency: normalizeCurrencyTotals(totals.totalCollectedByCurrencyMap, totals.totalCollected),
    pendingBalanceByCurrency: normalizeCurrencyTotals(totals.pendingBalanceByCurrencyMap, totals.pendingBalance),
  };
};

export const buildInvoicesKpiCards = (metricsValues, labels = {}) => {
  const safeMetrics = metricsValues && typeof metricsValues === 'object'
    ? metricsValues
    : createInitialInvoicesMetricsValues();

  const totalInvoicedByCurrency = normalizeCurrencyTotals(
    safeMetrics.totalInvoicedByCurrency,
    safeMetrics.totalInvoiced,
  );
  const totalInvoicedSummaryByCurrency = normalizeSummaryByCurrency(
    safeMetrics.totalInvoicedSummaryByCurrency,
    totalInvoicedByCurrency,
  );
  const totalCollectedByCurrency = normalizeCurrencyTotals(
    safeMetrics.totalCollectedByCurrency,
    safeMetrics.totalCollected,
  );
  const pendingBalanceByCurrency = normalizeCurrencyTotals(
    safeMetrics.pendingBalanceByCurrency,
    safeMetrics.pendingBalance,
  );
  const invoiceCount = toSafeCount(safeMetrics.invoiceCount);
  const invoiceAverage = toNumber(safeMetrics.invoiceAverage);
  const resolvedInvoiceAverage = invoiceAverage !== 0 || invoiceCount <= 0
    ? invoiceAverage
    : toNumber(safeMetrics.totalInvoiced) / invoiceCount;
  const invoiceCountByCurrency = normalizeCurrencyCounts(
    safeMetrics.invoiceCountByCurrency,
    invoiceCount,
    totalInvoicedByCurrency[0]?.currency || DEFAULT_CURRENCY,
  );
  const normalizedInvoiceAverageByCurrency = normalizeCurrencyTotals(safeMetrics.invoiceAverageByCurrency);
  const invoiceAverageByCurrency = normalizedInvoiceAverageByCurrency.length > 0
    ? normalizedInvoiceAverageByCurrency
    : buildAverageByCurrencyFromTotals(totalInvoicedByCurrency, invoiceCountByCurrency);
  const hasMultiCurrency = totalInvoicedByCurrency.length > 1;

  const totalInvoicedCards = buildTotalInvoicedKpiCards(
    safeMetrics,
    labels,
    totalInvoicedByCurrency,
    totalInvoicedSummaryByCurrency,
  );

  const totalCollectedSegments = hasMultiCurrency
    ? buildCurrencySegments(totalCollectedByCurrency, safeMetrics.totalCollected)
    : [];
  const pendingBalanceSegments = hasMultiCurrency
    ? buildCurrencySegments(pendingBalanceByCurrency, safeMetrics.pendingBalance)
    : [];
  const invoiceAverageSegments = hasMultiCurrency
    ? buildCurrencySegments(invoiceAverageByCurrency, resolvedInvoiceAverage)
    : [];
  const overdueInvoices = Math.max(0, Math.trunc(toNumber(safeMetrics.overdueInvoices)));
  const overdueLabelSource = String(labels.overdueInvoices || 'Facturas vencidas').trim();
  const overdueLabel = overdueLabelSource === ''
    ? 'facturas vencidas'
    : `${overdueLabelSource.charAt(0).toLowerCase()}${overdueLabelSource.slice(1)}`;
  const overdueInvoiceLine = {
    text: `\u26A0 ${formatInteger(overdueInvoices)} ${overdueLabel}`,
    tone: overdueInvoices > 0 ? 'warning' : 'muted',
  };

  return [
    ...totalInvoicedCards,
    {
      title: labels.totalCollected || 'Total cobrado',
      value: hasMultiCurrency
        ? (totalCollectedSegments[0] || formatCurrency(safeMetrics.totalCollected))
        : formatCurrency(safeMetrics.totalCollected),
      valueSegments: totalCollectedSegments,
      icon: 'fa-check-circle-o',
      iconBg: '#ecfcef',
      iconColor: '#1f9c3f',
    },
    {
      title: labels.pendingBalance || 'Saldo pendiente',
      value: hasMultiCurrency
        ? (pendingBalanceSegments[0] || formatCurrency(safeMetrics.pendingBalance))
        : formatCurrency(safeMetrics.pendingBalance),
      valueSegments: pendingBalanceSegments,
      valueMetaLines: [overdueInvoiceLine],
      icon: 'fa-clock-o',
      iconBg: '#fff7e8',
      iconColor: '#d88a1f',
    },
    {
      title: labels.averageInvoice || 'Factura promedio',
      value: hasMultiCurrency
        ? (invoiceAverageSegments[0] || formatCurrency(resolvedInvoiceAverage))
        : formatCurrency(resolvedInvoiceAverage),
      valueSegments: invoiceAverageSegments,
      icon: 'fa-calculator',
      iconBg: '#edf8f5',
      iconColor: '#178f72',
    },
  ];
};

export const mapInvoicesMetricsPayload = (payload) => {
  const safePayload = payload && typeof payload === 'object' ? payload : {};
  const summary = safePayload.summary && typeof safePayload.summary === 'object'
    ? safePayload.summary
    : {};
  const totalInvoicedSummarySource = summary.total_invoiced
    || summary.totalInvoiced
    || safePayload.total_invoiced_summary
    || safePayload.totalInvoicedSummary
    || null;

  const totalInvoiced = toNumber(safePayload.totalInvoiced ?? safePayload.total_invoiced);
  const invoiceCount = toSafeCount(safePayload.invoiceCount ?? safePayload.invoice_count);
  const totalCollected = toNumber(safePayload.totalCollected ?? safePayload.total_collected);
  const pendingBalance = toNumber(safePayload.pendingBalance ?? safePayload.pending_balance);

  const totalInvoicedByCurrency = normalizeCurrencyTotals(
    safePayload.totalInvoicedByCurrency
    ?? safePayload.total_invoiced_by_currency
    ?? summary.totalInvoicedByCurrency
    ?? summary.total_invoiced_by_currency,
    totalInvoiced,
  );

  const totalInvoicedSummaryByCurrency = normalizeSummaryByCurrency(
    safePayload.totalInvoicedSummaryByCurrency
    ?? safePayload.total_invoiced_summary_by_currency
    ?? summary.totalInvoicedByCurrency
    ?? summary.total_invoiced_by_currency,
    totalInvoicedByCurrency,
  );

  const totalCollectedByCurrency = normalizeCurrencyTotals(
    safePayload.totalCollectedByCurrency
    ?? safePayload.total_collected_by_currency,
    totalCollected,
  );

  const pendingBalanceByCurrency = normalizeCurrencyTotals(
    safePayload.pendingBalanceByCurrency
    ?? safePayload.pending_balance_by_currency,
    pendingBalance,
  );

  const invoiceCountByCurrency = normalizeCurrencyCounts(
    safePayload.invoiceCountByCurrency
    ?? safePayload.invoice_count_by_currency,
    invoiceCount,
    totalInvoicedByCurrency[0]?.currency || DEFAULT_CURRENCY,
  );

  const hasInvoiceAverage = safePayload.invoiceAverage != null || safePayload.invoice_average != null;
  const invoiceAverage = hasInvoiceAverage
    ? toNumber(safePayload.invoiceAverage ?? safePayload.invoice_average)
    : (invoiceCount > 0 ? toNumber(totalInvoiced / invoiceCount) : 0);

  const normalizedInvoiceAverageByCurrency = normalizeCurrencyTotals(
    safePayload.invoiceAverageByCurrency
    ?? safePayload.invoice_average_by_currency,
  );

  const invoiceAverageByCurrency = normalizedInvoiceAverageByCurrency.length > 0
    ? normalizedInvoiceAverageByCurrency
    : buildAverageByCurrencyFromTotals(totalInvoicedByCurrency, invoiceCountByCurrency);

  return {
    totalInvoiced,
    invoiceCount,
    invoiceAverage,
    totalCollected,
    pendingBalance,
    overdueInvoices: toNumber(safePayload.overdueInvoices ?? safePayload.overdue_invoices),
    totalInvoicedSummary: normalizeSummarySection(
      totalInvoicedSummarySource,
      safePayload.totalInvoiced ?? safePayload.total_invoiced
    ),
    totalInvoicedSummaryByCurrency,
    totalInvoicedByCurrency,
    invoiceAverageByCurrency,
    invoiceCountByCurrency,
    totalCollectedByCurrency,
    pendingBalanceByCurrency,
  };
};
